/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, memo, useMemo, useRef } from "react";
import { TextField } from "@material-ui/core";
import { FastField, getIn } from "formik";
import NumberFormat from "react-number-format";
import PropTypes from "prop-types";
import RequiredLabel from "./RequiredLabel";
import configDefaultForm from "./configDefault";

const GlobitsVNDCurrencyInputOnblur = (props) => (
  <FastField {...props} name={props.name} shouldUpdate={shouldComponentUpdate}>
    {({ field, meta, form }) => (
      <Component
        {...props}
        field={field}
        meta={meta}
        setFieldValue={form.setFieldValue}
      />
    )}
  </FastField>
);

const NumericFormatCustom = React.forwardRef(function NumericFormatCustom(
  props,
  ref
) {
  const { onChange, isDot, name, suffix, onRawValueChange, onBlur, ...other } =
    props;

  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange?.({
          target: {
            name,
            value: values.value, // raw value
          },
        });
        onRawValueChange?.(values.value); // raw number
      }}
      onBlur={onBlur}
      thousandSeparator={isDot ? "." : ","}
      decimalSeparator={isDot ? "," : "."}
      valueIsNumericString
      suffix={suffix}
    />
  );
});

NumericFormatCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  isDot: PropTypes.bool,
  onRawValueChange: PropTypes.func,
};

const Component = ({
  name,
  fullWidth = configDefaultForm.fullWidth,
  label,
  type = "text",
  debounceTime = 400, //default 0ms
  notDelay,
  field,
  meta,
  disabled,
  placeholder,
  isTextArea,
  minRowArea,
  required,
  requiredLabel,
  className = "",
  onChange,
  onBlur,
  setFieldValue,
  readOnly,
  autoCapitalize = false, // tự động viết hoa
  isDot = false,
  suffix = "₫",
  colorText,
  maxValue,
  roundNumbers = 4,
  ...rest
}) => {
  const rawValueRef = useRef(field.value ?? "");

  const handleRawValueChange = (val) => {
    if (val === undefined || val === null) return;
    if (+maxValue && +val > +maxValue) return;

    rawValueRef.current = val;
    setFieldValue(name, val);
  };

  const handleBlur = () => {
    setFieldValue(name, rawValueRef.current || null);
    onBlur?.(rawValueRef.current);
  };

  const displayLabel = useMemo(
    () =>
      label ? (
        <RequiredLabel
          label={label}
          requiredLabel={required || requiredLabel}
        />
      ) : null,
    [label, required, requiredLabel]
  );

  return (
    <>
      {!fullWidth && displayLabel && (
        <label htmlFor={name}>{displayLabel}</label>
      )}
      <TextField
        {...rest}
        multiline={isTextArea}
        id={name}
        name={name}
        value={field.value ?? ""}
        fullWidth
        label={fullWidth && displayLabel ? displayLabel : null}
        // onChange={handleChange}

        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        type={type}
        error={Boolean(meta && meta.touched && meta.error)}
        helperText={meta && meta.touched && meta.error ? meta.error : ""}
        InputProps={{
          readOnly: readOnly,
          inputComponent: NumericFormatCustom,

          inputProps: {
            ...(rest.InputProps?.inputProps || {}),
            isDot,
            name,
            // suffix,
            value: field.value ?? "",
            onRawValueChange: handleRawValueChange,
          },
          style: {
            color: colorText,
          },
          endAdornment: suffix && (
            <span style={{ marginLeft: "8px", color: "#757575" }}>
              {suffix}
            </span>
          ),
        }}
        InputLabelProps={{
          htmlFor: name,
          shrink: true,
        }}
        className={`input-container ${className} ${
          readOnly ? "read-only" : ""
        } ${isTextArea ? "isTextArea" : ""}`}
        minRows={minRowArea}
      />
    </>
  );
};

const shouldComponentUpdate = (nextProps, currentProps) =>
  nextProps?.readOnly !== currentProps?.readOnly ||
  nextProps?.value !== currentProps?.value ||
  nextProps?.onChange !== currentProps?.onChange ||
  nextProps?.disabled !== currentProps?.disabled ||
  nextProps?.name !== currentProps?.name ||
  nextProps?.onBlur !== currentProps?.onBlur ||
  nextProps?.maxValue !== currentProps?.maxValue ||
  Object.assign(nextProps).length !== Object.assign(currentProps).length ||
  getIn(nextProps.formik.values, currentProps.name) !==
    getIn(currentProps.formik.values, currentProps.name) ||
  getIn(nextProps.formik.errors, currentProps.name) !==
    getIn(currentProps.formik.errors, currentProps.name) ||
  getIn(nextProps.formik.touched, currentProps.name) !==
    getIn(currentProps.formik.touched, currentProps.name);

export default memo(GlobitsVNDCurrencyInputOnblur);
